/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import styles from './sidebarComponent.module.css';
import VersionText from './VersionText';

// SVG Icons
import { ReactComponent as SelectedAdminTop } from '../../assets/SVG/sidebar/selectedAdminTop.svg';
import { ReactComponent as MedicalIcon } from '../../assets/SVG/sidebar/selectedClinicalTop.svg';
import { ReactComponent as SemenProcessingIcon } from '../../assets/SVG/sidebar/selectedLab.svg';
import { ReactComponent as AdminIcon } from '../../assets/SVG/Admin.svg';
import { ReactComponent as ClinicalIcon } from '../../assets/SVG/sidebar/selectedClinical.svg';
import { ReactComponent as LabIcon } from '../../assets/SVG/sidebar/Lab.svg';
import { ReactComponent as AIIcon } from '../../assets/SVG/AiIcon.svg';
import { ReactComponent as SelectedAI } from '../../assets/SVG/SelectedAIIcon.svg';
import { ReactComponent as VidaiLogoIcon } from '../../assets/SVG/Vidai Updated Logo.svg';

// Styles and Images
import 'bootstrap/dist/css/bootstrap.min.css';
import DefaultImageMale from '../../assets/SVG/Default Profile - Male.svg';
import DefaultImageFemale from '../../assets/SVG/Default Profile - Female.svg';
import exitPatient from '../../assets/SVG/exitPatient.svg';

// Components
import ImageWithSkeleton from '../customImageComponent';

// Services and Types
import { getPatientName } from '../../services/apiService';
import { setPatientDetails } from '../../services/sharedService';
import { BasicPatientInfo } from '../../types';

// Contexts
import { usePatientContext } from '../patinetProvider';
import { usePatientContext as useSubPatientContext } from '../SubPatinetProvider';
import { usePrint } from '../PrintProvider';
import { checkModuleOrSubmodulePermission, getDefaultClinicalRedirect, getDefaultLabSubmoduleRedirect } from '../../utils/permissionService';
import { useTranslation } from 'react-i18next';
// import { permissions } from '../../constants/permissions';
import { getPermissionsFromLocalStorage, redirectToFirstEmbryology, redirectToFirstAndrology } from '../../utils/permissionService';

const SideBarComponent = () => {
  const [activeIndex, setActiveIndex] = useState(1);
  const [transformWidth, setTransformWidth] = useState(-4);
  const [selectedMenu, setSelectedMenu] = useState('admin');
  const [selectedSubMenu, setSelectedSubMenu] = useState('admin');
  const [mainmenu, setMainMenu] = useState('admin');
  const [activeclinicalmenu, setActiveclinicalmenu] = useState('');
  const [activelabmenu, setActiveLabmenu] = useState('');
  const [activeSettingMenu, setActiveSettingMenu] = useState('');
  const [selectedClinicalmenu, setselectedClinicalmenu] = useState('');
  const [selectedlabtaskmenu, setSelectedLabtaskmenu] = useState('');
  const [selectedSettingsMenu, setselectedSettingsMenu] = useState('');
  const getModuleAccess = (module: string, submodule?: string, typeName?: string) => checkModuleOrSubmodulePermission(module, submodule, typeName)?.AllowPermissions ?? false;
  const canAccessAdmin = getModuleAccess('Admin');
  const canAccessClinical = getModuleAccess('Clinical');
  const canAccessLab = getModuleAccess('Lab');
  const canAccessSettings = getModuleAccess('Settings');
  const [user, setUser] = useState<BasicPatientInfo | null>(null);
  const [userP, setUserP] = useState<BasicPatientInfo | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId, removePatient, addPatient } = usePatientContext();
  const { removeSubPatient, addSubPatient } = useSubPatientContext();
  const { TabOption,setTabOption } = usePrint();
  const { id } = useParams<{ id: string}>();
  // const allowedLabSidebarSections = React.useMemo(() => getAllowedLabSidebarSections(), []);
  const {t:tSidebar} = useTranslation('sidebar_config');
  useEffect(() => {
    if (patientId > 0) {
      getPatientName(patientId.toString()).then((response: BasicPatientInfo) => {
        setUserP({
          first_name: response.first_name,
          last_name: response.last_name,
          photo: response.photo,
          gender: response.gender
        });
      });
    } else {
      setUserP(null);
    }
     
  }, [patientId]);



  const changeMenu = useCallback((menuName: string): void => {;
    setMainMenu(menuName);
    if (menuName === 'admin' && location.pathname !== '/admin/appointments' && patientId <= 0) {
      navigate('/admin/appointments');
    }
    else if (menuName === 'admin' && patientId > 0){
      navigate(`/admin/registration/patient/${patientId}`);
    }
  }, [location.pathname, navigate, patientId]);

  const activesubmenu = useCallback( (e: string): void => {
    setSelectedSubMenu(e);
  },[]);

  const activeMenu = useCallback((e: string): void => {    
    setSelectedMenu(e);
  },[]);

  // Inventory navigaiton. 
  useEffect(() => {
    // Handle Inventory (Stock, Stock Transfer, GRN, Purchase) and their submenus
    if (location.pathname.startsWith('/admin/inventory')) {
      activeMenu('Inventory');
      const inventorySubmenus = [
        { path: '/admin/inventory/item-management', submenu: 'Stock' },
        { path: '/admin/inventory/stock-transfer', submenu: 'Stock Transfer' },
        { path: '/admin/inventory/grn', submenu: 'Grn' },
        { path: '/admin/inventory/adjustment', submenu: 'Adjustment' },
        { path: '/admin/inventory/purchase', submenu: 'Purchase' },
        { path: '/admin/inventory/return', submenu: 'Return' }
      ];
      const matched = inventorySubmenus.find(({ path }) => location.pathname === path || location.pathname.startsWith(path + '/'));
      if (matched) {
        activesubmenu(matched.submenu);
      }
    } else if (location.pathname.startsWith('/admin/vidai-leads/')) {
      setSelectedMenu('Vidai Leads');
    } else if (location.pathname.startsWith('/admin/refera')) {
      setSelectedMenu('Refera MD');
    } else if (location.pathname.startsWith('/admin/registration')) {
      setSelectedMenu('Registration');
    }else if (location.pathname.startsWith('/admin/billing')) { 
      setSelectedMenu('Billing');
    } else {
      setSelectedMenu('admin');
      setSelectedSubMenu('');
    }
  }, [location.pathname, activeMenu, activesubmenu]);
  
  
  // Function to handle submenu click
  const handleSubMenuClickItem = (submenu: string, path: string) => {
    activesubmenu(submenu);
    navigate(path);
  };

  const navToRegistration = () => {
    navigate(patientId > 0
      ? `/admin/registration/patient/${patientId}`
      : '/admin/registration/patient-list'
    );
  };

  const setActive = useCallback((index: number): void => {
    const transformMap: Record<number, number> = {
      1: -4,
      2: 57,
      3: 117,
      4: 175
    };
    setTransformWidth(transformMap[index] ?? -4);
    setActiveIndex(index);

    switch (index) {
    case 2:
      if (patientId > 0) {
        navigate(`clinical/patients/summary/${patientId}`);
      }

      break;
    case 3: {
      if (patientId > 0) {
        const permissions = getPermissionsFromLocalStorage();
        if (permissions && permissions.modules) {
          // Use permission-based redirect for Embryology
          redirectToFirstEmbryology(permissions.modules, patientId, navigate);
        } else {
          navigate(`lab/embryology/oocyte-grading/${patientId}`);
        }
      } else {
        navigate('/lab/dashboard');
      }
      break;
    }
    case 4:
      navigate('/ai/aiHub');  //changed by tejas saxena on 25-10-2025 for Ai hub redirection
      break;
    default:
      break;
    }
  }, [navigate, patientId]);

  useEffect(() => {
    if (location.pathname === '/') {
      changeMenu('admin');
    }
  }, [location.pathname, changeMenu]);

  const [pathArray, setPathArray] = useState([location.pathname]);

  useEffect(() => {
    const pathArray = location.pathname.split('/').filter(Boolean);
    setPathArray(pathArray);
    if (id) {      
      addPatient(Number(id));
      addSubPatient(Number(id));
    }

  }, [location.pathname,addPatient, id, addSubPatient]);


  useEffect(()=>{
    setTabOption({ ...TabOption, tabName: pathArray[2] });
    const menuMap: Record<string, { menu: string; index: number; width: number }> = {
      admin: { menu: 'admin', index: 1, width: -4 },
      clinical: { menu: 'clinical', index: 2, width: 57 },
      lab: { menu: 'lab', index: 3, width: 117 },
      ai: { menu: 'setting', index: 4, width: 175 }
    };
    const menuConfig = menuMap[pathArray[0]];
    if (menuConfig) {
      setMainMenu(menuConfig.menu);
      setActiveIndex(menuConfig.index);
      setTransformWidth(menuConfig.width);
    }
    if (location.pathname === '/clinical/dashboard') setActiveclinicalmenu('dashboard');
    if (location.pathname === '/lab/dashboard') setActiveLabmenu('Dashboard');
    if (location.pathname === '/lab/embryology') setActiveLabmenu('Embryology');

    
    if (location.pathname.startsWith('/ai/aiHub')) {
      setActiveSettingMenu('aiHub');
    }
    // Optimize AI configuration and reports menu selection
    else if (location.pathname.startsWith('/ai/configuration/')) {
      setActiveSettingMenu('configuration');
      if (location.pathname.startsWith('/ai/configuration/package-configuration')) {
        setselectedSettingsMenu('PackageConfig');
      } else if (location.pathname.startsWith('/ai/configuration/inventory-approval')) {
        setselectedSettingsMenu('InventoryApproval');
      } else if (location.pathname.includes('/ai/configuration/consent')) {
        setselectedSettingsMenu('Consent');
      } else if (location.pathname.includes('/ai/configuration/user')) {
        setselectedSettingsMenu('User');
      } else if (location.pathname.includes('/ai/configuration/clinic')) {
        setselectedSettingsMenu('Clinic');
      } else if (location.pathname.includes('/ai/configuration/page')) {
        setselectedSettingsMenu('Page');
      } else {
        setselectedSettingsMenu('');
      }
    } else if (location.pathname.startsWith('/ai/reports/clinical')) {
      setActiveSettingMenu('reports');
      setselectedSettingsMenu('clinical');
    }else if (location.pathname.startsWith('/ai/kpi_dashboard')) {
      setActiveSettingMenu('kpi');
      setselectedSettingsMenu('Clinical&Lab');
    } 

    if (location.pathname.includes('/ai/aiEmbryoGrader')) {
      setActiveSettingMenu('aiEmbryoGrader');
    }


    if (location.pathname === '/clinical/patients' && patientId > 0) {
      const clinicalNavMap: Record<string, string> = {
        'summary': `/clinical/patients/summary/${patientId}`,
        'medical-history': `/clinical/patients/medical-history/${patientId}`,
        'hpi': `/clinical/patients/hpi/${patientId}`,
        'examination': `/clinical/patients/examination/${patientId}`,
        'investigation': `/clinical/patients/investigation/${patientId}`,
        'treatment-plan': `clinical/patients/treatment-plan/${patientId}`,
        'stimulation': `/clinical/patients/stimulation/${patientId}`,
        'outcome': `/clinical/patients/outcome/${patientId}`,
        'document': `/clinical/patients/document/${patientId}`
      };
      const navPath = clinicalNavMap[pathArray[2]];
      if (navPath) navigate(navPath);
    }

    if (location.pathname === '/lab/andrology' && patientId > 0) {
      const andrologyNavMap: Record<string, string> = {
        'semen-analysis': `/lab/andrology/semen-analysis/${patientId}`,
        'semen-processing': `/lab/andrology/semen-processing/${patientId}`,
        'semen-freezing': `/lab/andrology/semen-freezing/${patientId}`,
        'semen-thawing': `/lab/andrology/semen-thawing/${patientId}`,
        'IUI': `/lab/andrology/IUI/${patientId}`
      };
      const navPath = andrologyNavMap[pathArray[2]];
      if (navPath) navigate(navPath);
    }

    if (location.pathname.startsWith('/lab/RFID')) {
      setActiveLabmenu('RFID');
      // Set selected RFID subtask based on subpath
      const sub = pathArray[2];
      if (sub === 'dashboard') {
        setSelectedLabtaskmenu('Dashboard');
      } else {
        // default to Patient List
        setSelectedLabtaskmenu('Patient List');
      }
    }
    const clinicalMenuMap: Record<string, string> = {
      'summary': 'summary',
      'medical-history': 'medical-history',
      'hpi': 'hpi',
      'examination': 'examination',
      'investigation': 'investigation',
      'treatment-plan': 'treatment-plan',
      'cycle-overview': 'cycle-overview',
      'stimulation': 'stimulation',
      'document': 'document',
      'outcome': 'outcome',
      'surgery': 'surgery'
    };
    if (clinicalMenuMap[pathArray[2]]) {
      setActiveclinicalmenu('Clinical Profile');
      setselectedClinicalmenu(clinicalMenuMap[pathArray[2]]);
    }
    const billingMenuMap: Record<string, string> = {
      'package': 'Package',
      'package-details': 'Package',
      'advance': 'Advance',
      'invoice': 'Invoice',
      'pharmacy': 'Invoice',
      'refund': 'Refund',
      // 'pharmacy': 'Pharmacy'
    };
    if (pathArray[0] === 'admin' && pathArray[1] === 'billing') {
      const billingSubmenu = billingMenuMap[pathArray[2]];
      setSelectedMenu('Billing');
      if (billingSubmenu) {
        setSelectedSubMenu(billingSubmenu);
      }
    }
    const labMenuMap: Record<string, { menu: string; task: string }> = {
      'semen-analysis': { menu: 'Andrology', task: 'Semen Analysis' },
      'semen-processing': { menu: 'Andrology', task: 'Semen Processing' },
      'semen-freezing': { menu: 'Andrology', task: 'Semen Freezing' },
      'semen-thawing': { menu: 'Andrology', task: 'Semen Thawing' },
      'IUI': { menu: 'Andrology', task: 'IUI' },
      'oocyte-grading': { menu: 'Embryology', task: 'Oocyte Grading' },
      'embryo-culture': { menu: 'Embryology', task: 'Embryo Culture' },
      'pgta': { menu: 'Embryology', task: 'PGT' },
      'cryo-thawing': { menu: 'Embryology', task: 'Cryo Thawing' },
      'oocyte': { menu: 'Cryostorage', task: 'Oocyte' },
      'embryo': { menu: 'Cryostorage', task: 'Embryo' },
      'sperm': { menu: 'Cryostorage', task: 'Sperm' }
    };
    const labConfig = labMenuMap[pathArray[2]];
    if (labConfig) {
      setActiveLabmenu(labConfig.menu);
      setSelectedLabtaskmenu(labConfig.task);
    }
    // eslint-disable-next-line 
  },[pathArray]);


  useEffect(() => {
    if (location.pathname.startsWith('/admin/pharmacy-dispense')) {
      activeMenu('pharmacy-dispense');
      activesubmenu('pharmacy-dispense');
    }


    if (location.pathname === '/admin/registration/patient-list') {
      activeMenu('Registration');
      activesubmenu('patient list');
    }
    // CIMA patient list highlighting
    if (location.pathname === '/admin/cima/patient-list') {
      activeMenu('CIMA');
      activesubmenu('patient list');
    }
    else if (location.pathname === '/admin/registration/patient-partner-registration') {
      activeMenu('Registration');
      activesubmenu('Registration Details');    
    }

    if (location.pathname === '/admin/appointments') {
      activeMenu('Calendar');
      activesubmenu('Consultation');
    }

    if (location.pathname === '/clinical/patients') {
      changeMenu('clinical');
      setActive(2);
      setActiveclinicalmenu('Clinical Profile');      
    }

    if (location.pathname.includes('admin/inventory')){
      activeMenu('Inventory');
    }
    if (location.pathname.includes('/admin/registration/patient') && location.pathname !== '/admin/registration/patient' && !sessionStorage.getItem('prevRoute')){
      sessionStorage.setItem('prevRoute','/admin/registration/patient-list');
    }
    if (location.pathname.includes('/clinical/patients')&& location.pathname !== '/clinical/patients' && !sessionStorage.getItem('prevRoute')){
      sessionStorage.setItem('prevRoute','/clinical/patients');
    }
    if (location.pathname.includes('/admin/pharmacy-dispense/prescribed-list/') && !sessionStorage.getItem('prevRoute')){
      sessionStorage.setItem('prevRoute','/admin/pharmacy-dispense');
    }
    // Set prevRoute for billing paths
    if (location.pathname.includes('/admin/billing/') && !sessionStorage.getItem('prevRoute')){
      sessionStorage.setItem('prevRoute','/admin/billing');
    }
    // Set prevRoute for lab embryology paths
    if (location.pathname.includes('/lab/embryology/') && !sessionStorage.getItem('prevRoute')){
      sessionStorage.setItem('prevRoute','/lab/dashboard');
    }
    // Set prevRoute for lab andrology paths
    if (location.pathname.includes('/lab/andrology/') && !sessionStorage.getItem('prevRoute')){
      sessionStorage.setItem('prevRoute','/lab/dashboard');
    }
    // Set prevRoute for appointments patient paths
    if (location.pathname.includes('/admin/appointments/patient') && !sessionStorage.getItem('prevRoute')){
      sessionStorage.setItem('prevRoute','/admin/appointments');
    }

    if (location.pathname.includes('/admin/registration/patient/') || location.pathname.includes('admin/appointments/patient') ) {
      const id = location.pathname.split('/').pop();
      if (id && Number(id) > 0) {
        getPatientName(id.toString()).then((response: BasicPatientInfo) => {
          setUser({
            first_name: response.first_name,
            last_name: response.last_name,
            photo: response.photo,
            gender: response.gender
          });
        });
      }
    } else {setUser(null);}
    //eslint-disable-next-line 
  }, [location.pathname]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const transformStyle = {
    transform: `translateX(${transformWidth}px)`,
  };

  const handleSubMenuClick = () => {
    navigate('/admin/Registration/patient-list');
  };

  const activeclinicaltask = (e: string): void => {
    setActiveclinicalmenu(e);
  };

  const activelabtask = (e: string): void => {
    setActiveLabmenu(e);
  };

  const selectedClinicaltask = (e: string): void => {
    setselectedClinicalmenu(e);
    setTabOption({tabName: e, subOption: '', reportOptions:[]});
  };

  const activelasbtask = (e: string): void => {
    setSelectedLabtaskmenu(e);
  };

  const exitPatientProfile = ():void =>{
    // Get prevRoute first before cleanup
    const prevRoute = sessionStorage.getItem('prevRoute');
    const currentPath = location.pathname;
    
    // Clean up ALL patient state and session data
    sessionStorage.removeItem('patientId');
    sessionStorage.removeItem('prevRoute');
    removePatient();
    removeSubPatient();
    setPatientDetails(null);
    setUserP(null);
    
    // Determine fallback based on current location context
    let fallbackRoute = '/admin/appointments'; // default fallback
    if (currentPath.startsWith('/clinical')) {
      fallbackRoute = '/clinical/patients';
    } else if (currentPath.startsWith('/lab')) {
      fallbackRoute = '/lab/dashboard';
    } else if (currentPath.includes('/admin/billing')) {
      fallbackRoute = '/admin/appointments';
    } else if (currentPath.includes('/admin/registration')) {
      fallbackRoute = '/admin/registration/patient-list';
    } else if (currentPath.includes('/admin/pharmacy-dispense')) {
      fallbackRoute = '/admin/pharmacy-dispense';
    } else if (currentPath.includes('/admin/appointments')) {
      fallbackRoute = '/admin/appointments';
    } else if (currentPath.includes('/admin/inventory/return/sales')) {
      fallbackRoute = '/admin/inventory/return/sales';
    }
    
    // Use prevRoute if valid, otherwise fallback
    const targetRoute = prevRoute && prevRoute.trim() !== '' && prevRoute !== 'null' && prevRoute !== 'undefined'
      ? prevRoute
      : fallbackRoute;
    navigate(targetRoute);
  };


  const handleQualityControlRedirect = () => {
    const loginUrl = 'https://stage1.vidaisolutions.com:2502/#/Login';
    
    // Encode the credentials in base64
    const encodedCredentials = 'SXNhYmVsbGE6SXNhQDIzI2I0NQ%3D%3D';
    
    // Encode the redirect target so special characters (#, /) are handled correctly

    // Append both redirect and credentials params to the login URL
    const targetUrl = `${loginUrl}?redirect=${encodedCredentials}`;

    // Open Fertivue in a new tab
    window.open(targetUrl, '_blank');
  };


  // const handleInventoryRedirect = () => {
  //   const loginUrl = 'https://stage1.vidaisolutions.com:2502/#/Login';

  //   // Encode the credentials in base64
  //   const encodedCredentials ='U29waGlhOlNwQDM0dCM%3D';

  //   // Create the target URL with encoded credentials
  //   const targetUrl = `${loginUrl}?redirect=${encodedCredentials}`;

  //   // Open inventory system in a new tab
  //   window.open(targetUrl, '_blank');
  // };

  function getUserNameAndProfilePic(): React.ReactNode {
    return (
      user && (
        <div className='d-flex'>
          <div>
            <img src={exitPatient} alt="exitPatient" width={'25px'} />
          </div>
          <div className={styles.userCard}>
            <ImageWithSkeleton src={user.photo} alt={'pc'} fallbackSrc={user.gender === 'FEMALE'? DefaultImageFemale: DefaultImageMale} height={'2rem'} width={'2rem'} borderRadius={'50%'}/>
            {/* <label>{tSidebar('patient_list')}</label> */}
            <label>{tSidebar('exit_patient')}</label>
          </div>
        </div>
      )
    );
  }

  return (
    <div className={styles.contained}>
      <div className={styles.navigation}>
        <ul className='p-0 w-100'>
          {canAccessAdmin && (
            <li
              className={`list ${activeIndex === 1 ? styles.active : ''}`}
              onClick={() => {
                setActive(1);
                changeMenu('admin');
              }}
            >
              <span>
                <span className={`icon ${activeIndex === 1 ? styles.clicked : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {activeIndex === 1 ? <SelectedAdminTop className={styles.svgclass} /> : <AdminIcon  className={styles.svgclass}/>}
                  <span style={{
                    fontSize: '12px',
                    color: activeIndex === 1 ? '#e17e61' : '#8F8888', // Dynamic color
                    fontWeight: activeIndex === 1 ? 600:500
                  }}>{tSidebar('admin')}</span>


                </span>
              </span>
            </li>
          )}

          {canAccessClinical  && (
            <li
              className={`list ${activeIndex === 2 ? styles.active : ''}`}
              onClick={() => {
                setActive(2);
                changeMenu('clinical');
                navigate(getDefaultClinicalRedirect(Number(id)));
              }}
            >
              <span>
                <span className={`icon ${activeIndex === 2 ? styles.clicked : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {activeIndex === 2 ? <MedicalIcon className={styles.svgclass} /> : <ClinicalIcon className={styles.svgclass} />}
                  <span style={{
                    fontSize: '12px',
                    color: activeIndex === 2 ? '#e17e61' : '#8F8888', // Dynamic color
                    fontWeight: activeIndex === 2 ? 600:500,
                  }}>{tSidebar('clinical')}</span>
                </span>
              </span>
            </li>
          )}

          {canAccessLab && (
            <li
              className={`list ${activeIndex === 3 ? styles.active : ''}`}
              onClick={() => {
                setActive(3);
                changeMenu('lab');                
                navigate(getDefaultLabSubmoduleRedirect(patientId));
              }}
            >
              <span>
                <span className={`icon ${activeIndex === 3 ? styles.clicked : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {activeIndex === 3 ? <SemenProcessingIcon className={styles.svgclass} /> : <LabIcon className={styles.svgclass} />}
                  <span style={{
                    fontSize: '12px',
                    color: activeIndex === 3 ? '#e17e61' : '#8F8888', // Dynamic color
                    fontWeight: activeIndex === 3 ? 600:500,
                  }}>{tSidebar('lab')}</span>
                </span>
              </span>
            </li>
          )}

          {canAccessSettings && (
            <li
              className={`list ${activeIndex === 4 ? styles.active : ''}`}
              onClick={() => {
                setActive(4);
                changeMenu('setting');
              }}
            >
              <span>
                <span className={`icon ${activeIndex === 4 ? styles.clicked : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {activeIndex === 4 ? <SelectedAI className={styles.svgclass} /> : <AIIcon className={styles.svgclass} />}
                  <span style={{
                    fontSize: '12px',
                    color: activeIndex === 4 ? '#e17e61' : '#8F8888', // Dynamic color
                    fontWeight: activeIndex === 4 ? 600:500,
                  }}>{tSidebar('ai')}</span>
                </span>
              </span>
            </li>
          )}

          {/* <svg
            style={{ fill: 'rgb(248, 247, 247)', ...transformStyle }}
            className={styles.indicator}
            width='94'
            height='56'
          >
            <ellipse cx='47' cy='30' rx='24' ry='30' />
            <path d='M24 20C24 20 28 55.9999 48 56L0 55.9997C18 55.9998 24 20 24 20Z' />
            <path d='M70 20C70 20 66 55.9999 46 56L94 55.9997C76 55.9998 70 20 70 20Z' />
          </svg> */}
        </ul>
      </div>
      {mainmenu === 'admin' && (
        <div className={styles.navigationsidebar}>
          <VidaiLogoIcon
            style={{ position: 'absolute', bottom: '8%', width: '60%', left: '50px' }}
          />
          <VersionText   />
          <div className={`${styles.adminmenu} row`} style={{ '--bs-gutter-x': '0' } as React.CSSProperties}>
            <ul className={`${styles.sidebarunorderedList} text-muted bg-white px-0`}>

              {/* Calendar Section */}
              {getModuleAccess('Admin', 'Calendar') && (
                <div className={styles.orderlistDiv}>
                  {location.pathname.includes('admin/appointments/patient') ? (
                    <li
                      className={selectedMenu === 'Calendar-patient' ? styles.boldClass : styles.orderlistLi}
                      onClick={() => {
                        exitPatientProfile();
                      }}
                    >
                      {getUserNameAndProfilePic()}
                    </li>
                  ) : (
                    <li
                      className={selectedMenu === 'Calendar' ? styles.boldClass : styles.orderlistLi}
                      onClick={() => {
                        if (patientId > 0) {
                          activeMenu('Calendar-patient');
                          navigate(`/admin/appointments/patient/${patientId}`);
                        } else {
                          navigate('/admin/appointments');
                          activeMenu('Calendar');
                        }
                      }}
                    >
                      {tSidebar('calendar')}
                    </li>
                  )}
                </div>
              )}
              {!userP &&getModuleAccess('Admin', 'Refera MD') && (
                <div className={styles.orderlistDiv}>
                  <li
                    className={selectedMenu === 'Refera MD' ? styles.boldClass : styles.orderlistLi}
                    onClick={() => {
                      activeMenu('Refera MD');
                      navigate('/admin/refera/patient-list');
                    }}
                  >
                    {tSidebar('refera', { defaultValue: 'Refera MD' })}
                  </li>
                </div>
              )}

              {!userP &&getModuleAccess('Admin', 'Vidai Leads') && (

                <div className={styles.orderlistDiv}>
                  <li
                    className={selectedMenu === 'Vidai Leads' ? styles.boldClass : styles.orderlistLi}
                    onClick={() => {
                      activeMenu('Vidai Leads');
                      navigate('/admin/vidai-leads/patient-list');
                    }}
                  >
                    {tSidebar('vidai_leads', { defaultValue: 'Vidai Leads' })}
                  </li>
                </div>
              )}
              {/* Registration Section */}
              {getModuleAccess('Admin', 'Registration') && (
                <div className={styles.orderlistDiv}>
                  {user && location.pathname.includes('admin/registration/patient') ? (
                    <li
                      className={selectedMenu === 'GenderProfile' ? styles.boldClass : styles.orderlistLi}
                      onClick={() => {
                        exitPatientProfile();
                      }}
                    >
                      {getUserNameAndProfilePic()}
                    </li>
                  ) : (
                    <li
                      className={selectedMenu === 'Registration' ? styles.boldClass : styles.orderlistLi}
                      onClick={() => {
                        activeMenu('Registration');
                        navToRegistration();
                      }}
                    >
                      {tSidebar('registration')}
                    </li>
                  )}
                </div>
              )}

              {/* Registration Details Submenu (hidden) */}
              {getModuleAccess('Admin', 'Registration') && selectedMenu === 'Registration' && location.pathname === '/admin/registration/patient-partner-registration' && (
                <div className={styles.mainclass}>
                  <ul className={styles.subunorderList} style={{ display: 'none' }}>
                    <li
                      className={selectedSubMenu === 'Registration Details' ? `${styles.subunorderedlistLiChecked} ${styles.active}` : styles.subunorderedlistLi}
                      onClick={() => activesubmenu('Registration Details')}
                    >
                      <label onClick={handleSubMenuClick}>{tSidebar('registration_details')}</label>
                    </li>
                  </ul>
                </div>
              )}
              {getModuleAccess('Admin', 'Pharmacy Dispense') && (

                <div className={styles.orderlistDiv}>
                  <li
                    className={selectedMenu === 'pharmacy-dispense' ? styles.boldClass : styles.orderlistLi}
                    onClick={() => {
                      activeMenu('pharmacy-dispense');
                      navigate(patientId > 0 ? `/admin/pharmacy-dispense/new/${patientId}` : '/admin/pharmacy-dispense');
                    }}
                  >
                    {userP && selectedMenu === 'pharmacy-dispense' ? (
                      <div className="d-flex" style={{ alignItems: 'center' }} onClick={(e) => { e.stopPropagation(); exitPatientProfile(); }}>
                        <div >
                          <img src={exitPatient} alt="exitPatient" width={'25px'} />
                        </div>
                        <div className={styles.userCard}>
                          <ImageWithSkeleton
                            src={userP.photo}
                            alt={'pc'}
                            fallbackSrc={userP.gender === 'FEMALE' ? DefaultImageFemale : DefaultImageMale}
                            height={'2rem'}
                            width={'2rem'}
                            borderRadius={'50%'}
                          />
                          <label>{tSidebar('exit_patient')}</label>
                        </div>
                      </div>
                    ) : (
                      tSidebar('pharmacy_dispense')
                    )}
                  </li>
                </div>

              )}

              {/* {!userP &&( */}
              <>

                {/* Billing Section and Submenus */}
                {getModuleAccess('Admin', 'Billing') && (
                  <div className={styles.orderlistDiv}>

                    <li
                      className={selectedMenu === 'Billing' ? styles.boldClass : styles.orderlistLi}
                      style={selectedMenu === 'Billing' ?{borderRadius:'0px 0px 20px 0px'}:{borderRadius:'0px 0px 0px 0px'}}

                    >
                      {userP && selectedMenu === 'Billing' ? (
                        <div className="d-flex" style={{ alignItems: 'center' }} onClick={() => exitPatientProfile()}>
                          <div >
                            <img src={exitPatient} alt="exitPatient" width={'25px'} />
                          </div>
                          <div className={styles.userCard}>
                            <ImageWithSkeleton
                              src={userP.photo}
                              alt={'pc'}
                              fallbackSrc={userP.gender === 'FEMALE' ? DefaultImageFemale : DefaultImageMale}
                              height={'2rem'}
                              width={'2rem'}
                              borderRadius={'50%'}
                            />
                          </div>
                          <div style={{ paddingLeft: '0.5rem' }} >
                            {/* <p>{tSidebar('patient_profile')}</p> */}
                            <p>{tSidebar('exit_patient')}</p>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div
                            className="d-flex"
                            onClick={() => {
                              activeMenu('Billing');
                              if (userP && patientId) {
                                navigate(`/admin/billing/package/patient-package/${patientId}`);
                              } else {
                                navigate('/admin/billing/package');
                              }
                            }}
                          >
                            <div>
                              <p>{tSidebar('billing')}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>

                    {userP && selectedMenu === 'Billing' && (
                      <div className={styles.mainclass} style={{marginTop:'10px'}}>
                        <ul className={styles.subunorderList}>
                          {[{ name: 'Package', path: `/admin/billing/package/patient-package/${patientId}`, translation: 'package' },
                            { name: 'Invoice', path: `/admin/billing/invoice/patient-invoice/${patientId}`, translation: 'invoice' },
                            { name: 'Advance', path: `/admin/billing/advance/patient-advance/${patientId}`, translation: 'advance' },
                            { name: 'Refund', path: `/admin/billing/refund/patient-refund/${patientId}`, translation: 'refund' },
                            // { name: 'Pharmacy', path: `/admin/billing/pharmacy/patient-pharmacy/${patientId}`, translation: 'pharmacy' }
                          ].filter(({ name }) => getModuleAccess('Admin','Billing', name))
                            .map(({ name, path, translation }) => (
                              <li
                                key={name}
                                className={selectedSubMenu === name ? `${styles.subunorderedlistLiChecked} ${styles.active}` : styles.subunorderedlistLi}
                                onClick={() => handleSubMenuClickItem(name, path)}
                              >
                                {tSidebar(translation)}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                  

                {!userP && getModuleAccess('Admin', 'Inventory') && (

                  <div className={`${styles.orderlistDiv}`}>
                    <li
                      className={`${selectedMenu === 'Inventory' ? styles.boldClass : styles.orderlistLi}`}
                      onClick={() => navigate('/admin/inventory/item-management/stock')}
                    >
                      {tSidebar('inventory')}
                    </li>
                    {selectedMenu === 'Inventory' && (
                      <div className={styles.mainclass}>
                        <ul className={styles.subunorderList}>
                          {[{ name: 'Stock', path: '/admin/inventory/item-management', translation: 'stock' },
                            { name: 'Stock Transfer', path: '/admin/inventory/stock-transfer', translation: 'stock_transfer' },
                            { name: 'Purchase', path: '/admin/inventory/purchase/requisition', translation: 'purchase' },
                            { name: 'Grn', path: '/admin/inventory/grn/prospective', translation: 'grn' },
                            { name: 'Return', path: '/admin/inventory/return', translation: 'return' },
                            { name: 'Adjustment', path: '/admin/inventory/adjustment', translation: 'adjustment' }
                          ].filter(({ name }) => getModuleAccess('Admin', name))
                            .map(({ name, path, translation }) => (
                              <li
                                key={name}
                                className={selectedSubMenu === name ? `${styles.subunorderedlistLiChecked} ${styles.active}` : styles.subunorderedlistLi}
                                onClick={() => handleSubMenuClickItem(name, path)}
                              >
                                {tSidebar(translation)}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Inventory for Patient - Only Return */}
                {userP && getModuleAccess('Admin', 'Inventory') && getModuleAccess('Admin', 'Return') && (
                  <div className={`${styles.orderlistDiv}`}>
                    <li
                      className={`${selectedMenu === 'Inventory' ? styles.boldClass : styles.orderlistLi}`}
                      style={selectedMenu === 'Inventory' ? { borderRadius: '0px 0px 20px 0px' } : { borderRadius: '0px 0px 0px 0px' }}
                    >
                      {selectedMenu === 'Inventory' ? (
                        <div className="d-flex" style={{ alignItems: 'center' }} onClick={() => exitPatientProfile()}>
                          <div>
                            <img src={exitPatient} alt="exitPatient" width={'25px'} />
                          </div>
                          <div className={styles.userCard}>
                            <ImageWithSkeleton
                              src={userP.photo}
                              alt={'pc'}
                              fallbackSrc={userP.gender === 'FEMALE' ? DefaultImageFemale : DefaultImageMale}
                              height={'2rem'}
                              width={'2rem'}
                              borderRadius={'50%'}
                            />
                          </div>
                          <div style={{ paddingLeft: '0.5rem' }}>
                            <p>{tSidebar('exit_patient')}</p>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="d-flex"
                          onClick={() => {
                            activeMenu('Inventory');
                            navigate(`/admin/inventory/return/patient-sales/${patientId}`);
                          }}
                        >
                          <div>
                            <p>{tSidebar('inventory')}</p>
                          </div>
                        </div>
                      )}
                    </li>
                    {selectedMenu === 'Inventory' && (
                      <div className={styles.mainclass} style={{ marginTop: '10px' }}>
                        <ul className={styles.subunorderList}>
                          <li
                            className={selectedSubMenu === 'Return' ? `${styles.subunorderedlistLiChecked} ${styles.active}` : styles.subunorderedlistLi}
                            onClick={() => handleSubMenuClickItem('Return', `/admin/inventory/return/patient-sales/${patientId}`)}
                          >
                            {tSidebar('return')}
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}

            
              </>
            </ul>
          </div>
        </div>
      )}

      {mainmenu === 'clinical' && (
        <div className={styles.navigationsidebar}>
          <VidaiLogoIcon
            style={{ position: 'absolute', bottom: '8%', width: '60%', left: '50px' }}
          />
          <VersionText   />
          <div className={`${styles.adminmenu} row`} style={{ '--bs-gutter-x': '0' } as React.CSSProperties}>
            <div>
              <ul className={`${styles.sidebarunorderedList} text-muted bg-white p-0`} style={{ borderRadius: '30px' }}>
                <div
                  style={
                    userP
                      ? {
                        background:
                            'linear-gradient(199.55deg, #EEEEEE 20.06%, rgba(243, 243, 243, 0.547773) 75.62%, rgba(232, 234, 236, 0) 110.17%)',
                        borderRadius: '30px',
                      }
                      : { borderRadius: '30px' }
                  }
                >
                  <div className={`${styles.orderlistDiv} py-3`} style={{ borderRadius: '20px 20px 20px 0px' }}>
                    {!userP && getModuleAccess('Clinical', 'Clinical Dashboard') && (
                      <li
                        className={
                          activeclinicalmenu === 'dashboard'
                            ? (userP ? styles.boldClass1 : styles.boldClass)
                            : styles.orderlistLi
                        }
                      >
                        <div className="d-flex" onClick={() => { activeclinicaltask('dashboard'); navigate('/clinical/dashboard'); }}>
                          <div>
                            <p>{tSidebar('dashboard')}</p>
                          </div>
                        </div>
                      </li>
                    )}
                    <li
                      className={
                        activeclinicalmenu === 'Clinical Profile'
                          ? (userP ? styles.boldClass1 : styles.boldClass)
                          : styles.orderlistLi
                      }
                    >
                      {userP ? (
                        <div className="d-flex" style={{ alignItems: 'center' }} onClick={() => exitPatientProfile()}>
                          <div >
                            <img src={exitPatient} alt="exitPatient" width={'25px'} />
                          </div>
                          <div className={styles.userCard}>
                            <ImageWithSkeleton
                              src={userP.photo}
                              alt={'pc'}
                              fallbackSrc={userP.gender === 'FEMALE' ? DefaultImageFemale : DefaultImageMale}
                              height={'2rem'}
                              width={'2rem'}
                              borderRadius={'50%'}
                            />
                          </div>
                          <div style={{ paddingLeft: '0.5rem' }} >
                            {/* <p>{tSidebar('patient_profile')}</p> */}
                            <p>{tSidebar('exit_patient')}</p>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="d-flex" onClick={() => { activeclinicaltask('Clinical Profile'); navigate('/clinical/patients'); }}>
                            <div>
                              <p>{tSidebar('patient_list')}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  </div>
                  {/* Clinical Profile Submenus */}
                  {activeclinicalmenu === 'Clinical Profile' && patientId > 0 && (
                    <div style={{ backgroundColor: '#FFF' }}>
                      <ul className={styles.subunorderList}>
                        {(() => {
                          const items = [
                            { key: 'summary', name: 'Summary', label: tSidebar('summary'), path: `/clinical/patients/summary/${patientId}` },
                            { key: 'medical-history', name: 'Medical History', label: tSidebar('medical_history'), path: `/clinical/patients/medical-history/${patientId}` },
                            { key: 'hpi', name: 'HPI', label: tSidebar('hpi'), path: `/clinical/patients/hpi/${patientId}` },
                            { key: 'examination', name: 'Examination', label: tSidebar('examination'), path: `/clinical/patients/examination/${patientId}` },
                            { key: 'investigation', name: 'Investigations', label: tSidebar('investigations'), path: `/clinical/patients/investigation/${patientId}` },
                            { key: 'treatment-plan', name: 'Treatment Plan', label: tSidebar('treatment_plan'), path: `/clinical/patients/treatment-plan/${patientId}` },
                            { key: 'cycle-overview', name: 'Cycle Overview', label: tSidebar('cycle_overview'), path: `/clinical/patients/cycle-overview/${patientId}` },
                            { key: 'stimulation', name: 'Stimulation', label: tSidebar('stimulation'), path: `/clinical/patients/stimulation/${patientId}` },
                            { key: 'surgery', name: 'Surgery', label: tSidebar('surgery'), path: `/clinical/patients/surgery/${patientId}` },
                            { key: 'outcome', name: 'Outcome', label: tSidebar('outcome'), path: `/clinical/patients/outcome/${patientId}` },
                            { key: 'document', name: 'Document', label: tSidebar('document'), path: `/clinical/patients/document/${patientId}` },
                          ].filter(item => getModuleAccess('Clinical', item.name));

                          // Find the highest-priority available key for the divider
                          const dividerPriority = ['investigation', 'examination', 'hpi', 'medical-history'];
                          let dividerIndex = -1;
                          for (const key of dividerPriority) {
                            dividerIndex = items.findIndex(item => item.key === key);
                            if (dividerIndex !== -1) break;
                          }
 
                          return items.map(({ key, label, path }, idx) => (
                            <React.Fragment key={key}>
                              <li
                                className={
                                  selectedClinicalmenu === key
                                    ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                                    : styles.subunorderedlistLi
                                }
                                onClick={() => {
                                  selectedClinicaltask(key);
                                  navigate(path);
                                }}
                              >
                                {label}
                              </li>
                              {idx === dividerIndex && <hr key={`divider-${key}`} />}
                            </React.Fragment>
                          ));
                        })()}
                      </ul>
                    </div>
                  )}
                </div>
                                  
                {/* Treatment Submenus */}
                {activeclinicalmenu === 'Treatment' && (
                  <div>
                    <ul className={styles.subunorderList}>
                      {[
                        { key: 'Treatment Plan', label: 'Treatment Plan' },
                        { key: 'ART Cycle', label: 'ART Cycle' },
                        { key: 'Stimulation', label: 'Stimulation' },
                        { key: 'Surgery', label: 'Surgery' },
                        { key: 'Outcome', label: 'Outcome' },
                      ].map(({ key, label }) => (
                        <li
                          key={key}
                          className={
                            selectedClinicalmenu === key
                              ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                              : styles.subunorderedlistLi
                          }
                          onClick={() => selectedClinicaltask(key)}
                        >
                          {label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {mainmenu === 'lab' && (
        <div className={styles.navigationsidebar}>
          <VidaiLogoIcon
            style={{ position: 'absolute', bottom: '8%', width: '60%', left: '40px' }}
          />
          <VersionText   />
          <div className={`${styles.adminmenu} row`} style={{ '--bs-gutter-x': '0' } as React.CSSProperties}>
            <ul className={`${styles.sidebarunorderedList} text-muted bg-white px-0`}>
              {/* Dashboard Section */}
              <div
                style={
                  userP
                    ? {
                      background:
                          'linear-gradient(199.55deg, #EEEEEE 20.06%, rgba(243, 243, 243, 0.547773) 75.62%, rgba(232, 234, 236, 0) 110.17%)',
                      borderRadius: '30px',
                    }
                    : { borderRadius: '30px' }
                }
              >
                {!userP && getModuleAccess('Lab', 'Lab Dashboard') && (
                  <div className={`${styles.orderlistDiv}`} style={{ borderRadius: '20px 20px 20px 0px' }}>
                    <li
                      className={
                        activelabmenu === 'Dashboard'
                          ? (userP ? styles.boldClass1 : styles.boldClass)
                          : styles.orderlistLi
                      }
                    >
                      <div onClick={() => { activelasbtask('Dashboard'); navigate('/lab/dashboard'); }}>
                        <p>{tSidebar('dashboard')}</p>
                      </div>
                    </li>
                  </div>
                )}

                {/* Embryology Section */}
                {getModuleAccess('Lab','Embryology') && (

                  <div className={`${styles.orderlistDiv}`} style={{ borderRadius: '20px 20px 20px 0px' }}>
                    <li
                      className={
                        activelabmenu === 'Embryology'
                          ? (userP ? styles.boldClass1 : styles.boldClass)
                          : styles.orderlistLi
                      }
                    >
                      {userP && activelabmenu === 'Embryology' ? (
                        <div className="d-flex" style={{ alignItems: 'center' }} onClick={() => exitPatientProfile()}>
                          <div >
                            <img src={exitPatient} alt="exitPatient" width={'25px'} />
                          </div>
                          <div className={styles.userCard} >
                            <ImageWithSkeleton
                              src={userP.photo}
                              alt={'pc'}
                              fallbackSrc={userP.gender === 'FEMALE' ? DefaultImageFemale : DefaultImageMale}
                              height={'2rem'}
                              width={'2rem'}
                              borderRadius={'50%'}
                            />
                          </div>
                          <div style={{ paddingLeft: '0.5rem' }} >
                            <p>{tSidebar('exit_patient')}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex" onClick={() => {
                          activelabtask('Embryology');
                          if (patientId > 0) {
                            const permissions = getPermissionsFromLocalStorage();
                            if (permissions) {
                              redirectToFirstEmbryology(permissions.modules, patientId, navigate);
                            } else {
                              navigate(`/lab/embryology/oocyte-grading/${patientId}`);
                            }
                          } else {
                            navigate('/lab/embryology');
                          }
                        }}>
                          <div>
                            <p>{tSidebar('embryology')}</p>
                          </div>
                        </div>
                      )}
                    </li>
                  </div>
                )}

                {/* Embryology Submenus */}
                {activelabmenu === 'Embryology' && patientId > 0 && (
                  <div style={{ backgroundColor: '#FFF' }}>
                    <ul className={styles.subunorderList}>
                      {[
                        { key: 'Oocyte Grading', name: 'Oocyte Grading', label: tSidebar('oocyte_grading'), path: `/lab/embryology/oocyte-grading/${patientId}` },
                        { key: 'Embryo Culture', name: 'Embryo Culture', label: tSidebar('embryo_culture'), path: `/lab/embryology/embryo-culture/${patientId}` },
                        { key: 'AI Embryo', name: 'AI Embryo', label: tSidebar('ai_embryo'), path: `/lab/embryology/ai-embryo/${patientId}` },
                        { key: 'PGT', name: 'PGT', label: tSidebar('pgt'), path: `/lab/embryology/pgta/${patientId}` },
                        { key: 'Cryo Thawing', name: 'Cryo Thawing', label: tSidebar('cryo_thawing'), path: `/lab/embryology/cryo-thawing/${patientId}` },
                      ]
                        .filter(item => getModuleAccess('Lab', item.name))
                        .map(({ key, name, label, path }) => (
                          <li
                            key={key}
                            className={
                              selectedlabtaskmenu === name
                                ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                                : styles.subunorderedlistLi
                            }
                            onClick={() => {
                              activelasbtask(name);
                              navigate(path);
                            }}
                          >
                            {label}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Andrology Section */}
              
              <div
                style={
                  userP
                    ? {
                      background:
                          'linear-gradient(199.55deg, #EEEEEE 20.06%, rgba(243, 243, 243, 0.547773) 75.62%, rgba(232, 234, 236, 0) 110.17%)',
                      borderRadius: '30px',
                    }
                    : { borderRadius: '30px' }
                }
              >

                {getModuleAccess('Lab','Andrology') && (
                  <div className={`${styles.orderlistDiv}`} style={{ borderRadius: '20px 20px 20px 0px' }}>
                    <li
                      className={
                        activelabmenu === 'Andrology'
                          ? (userP ? styles.boldClass1 : styles.boldClass)
                          : styles.orderlistLi
                      }
                    >
                      {userP && activelabmenu === 'Andrology' ? (
                        <div className="d-flex" style={{ alignItems: 'center' }} onClick={() => exitPatientProfile()}>
                          <div >
                            <img src={exitPatient} alt="exitPatient" width={'25px'} />
                          </div>
                          <div className={styles.userCard} >
                            <ImageWithSkeleton
                              src={userP.photo}
                              alt={'pc'}
                              fallbackSrc={userP.gender === 'FEMALE' ? DefaultImageFemale : DefaultImageMale}
                              height={'2rem'}
                              width={'2rem'}
                              borderRadius={'50%'}
                            />
                          </div>
                          <div style={{ paddingLeft: '0.5rem' }} >
                            <p>{tSidebar('exit_patient')}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex" onClick={() => {
                          activelabtask('Andrology');
                          if (patientId > 0) {
                            const permissions = getPermissionsFromLocalStorage();
                            if (permissions) {
                              // Use permission-based redirect for Andrology
                              redirectToFirstAndrology(permissions.modules, patientId, navigate);
                            } else {
                              navigate(`/lab/andrology/semen-analysis/${patientId}`);
                            }
                          } else {
                            navigate('/lab/andrology');
                          }
                        }}>
                          <div>
                            <p>{tSidebar('andrology')}</p>
                          </div>
                        </div>
                      )}
                    </li>
                  </div>
                )}
                {/* Andrology Submenus */}
                {activelabmenu === 'Andrology' && patientId > 0 && (
                  <div style={{ backgroundColor: '#FFF' }}>
                    <ul className={styles.subunorderList}>
                      {[
                        { key: 'Semen Analysis', name: 'Semen Analysis', label: tSidebar('semen_analysis'), path: `/lab/andrology/semen-analysis/${patientId}` },
                        { key: 'Semen Processing', name: 'Semen Processing', label: tSidebar('semen_processing'), path: `/lab/andrology/semen-processing/${patientId}` },
                        { key: 'Semen Freezing', name: 'Semen Freezing', label: tSidebar('semen_freezing'), path: `/lab/andrology/semen-freezing/${patientId}` },
                        { key: 'Semen Thawing', name: 'Semen Thawing', label: tSidebar('semen_thawing'), path: `/lab/andrology/semen-thawing/${patientId}` },
                        { key: 'IUI', name: 'IUI', label: tSidebar('iui'), path: `/lab/andrology/IUI/${patientId}` },
                      ]
                        .filter(item => getModuleAccess('Lab', item.name))
                        .map(({ key, name, label, path }) => (
                          <li
                            key={key}
                            className={
                              selectedlabtaskmenu === name
                                ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                                : styles.subunorderedlistLi
                            }
                            onClick={() => {
                              activelasbtask(name);
                              navigate(path);
                            }}
                          >
                            {label}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* RFID Section */}
              {/* {!userP && getModuleAccess('Lab','RFID') && ( */}
              {!userP  && (
                <div className={`${styles.orderlistDiv}`} style={{ borderRadius: '20px 20px 20px 0px' }}>
                  <li
                    className={
                      activelabmenu === 'RFID'
                        ? (userP ? styles.boldClass1 : styles.boldClass)
                        : styles.orderlistLi
                    }
                  >
                    <div className="d-flex" onClick={() => { activelabtask('RFID'); if (patientId > 0) { navigate('/lab/RFID'); } else { navigate('/lab/RFID'); } }}>
                      <div>
                        <p>{tSidebar('Audity')}</p>               
                      </div>
                    </div>
                  </li>
                </div>
              )}

              {/* RFID Submenus */}
              {activelabmenu === 'RFID' && (
                <div style={{ backgroundColor: '#FFF' }}>
                  <ul className={styles.subunorderList}>
                    {[
                      { key: 'dashboard', name: 'Dashboard', label: tSidebar('dashboard'), path: '/lab/RFID/dashboard' },
                      { key: 'patient-list', name: 'Patient List', label: tSidebar('patient_list'), path: '/lab/RFID/patient-list' },
                    ].map(({ key, name, label, path }) => (
                      <li
                        key={key}
                        className={
                          selectedlabtaskmenu === name
                            ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                            : styles.subunorderedlistLi
                        }
                        onClick={() => {
                          activelasbtask(name);
                          navigate(path);
                        }}
                      >
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Cryostorage Section */}
              {!userP && getModuleAccess('Lab','Cryostorage') && (
                <div className={`${styles.orderlistDiv}`} style={{ borderRadius: '20px 20px 20px 0px' }}>
                  <li
                    className={
                      activelabmenu === 'Cryostorage'
                        ? (userP ? styles.boldClass1 : styles.boldClass)
                        : styles.orderlistLi
                    }
                  >
                    <div className="d-flex" onClick={() => { activelabtask('Cryostorage'); if (patientId > 0) { navigate('/lab/Cryostorage/oocyte'); } else { navigate('/lab/Cryostorage/oocyte'); } }}>
                      <div>
                        <p>{tSidebar('cryostorage')}</p>
                      </div>
                    </div>
                  </li>
                </div>
              )}

              {/* Cryostorage Submenus */}
              {activelabmenu === 'Cryostorage' && (
                <div style={{ backgroundColor: '#FFF' }}>
                  <ul className={styles.subunorderList}>
                    {[
                      { key: 'oocyte', name: 'Oocyte', label: tSidebar('oocyte'), path: '/lab/cryostorage/oocyte' },
                      { key: 'embryo', name: 'Embryo', label: tSidebar('embryo'), path: '/lab/cryostorage/embryo' },
                      { key: 'sperm', name: 'Sperm', label: tSidebar('sperm'), path: '/lab/cryostorage/sperm' },
                    ]
                      .filter(item => getModuleAccess('Lab','Cryostorage',item.name))
                      .map(({ key, name, label, path }) => (
                        <li
                          key={key}
                          className={
                            selectedlabtaskmenu === name
                              ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                              : styles.subunorderedlistLi
                          }
                          onClick={() => {
                            activelasbtask(name);
                            navigate(path);
                          }}
                        >
                          {label}
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {!userP &&(
                <div className={`${styles.orderlistDiv}`} style={{ borderRadius: '20px 20px 20px 0px' }}>
                  <li
                    className={
                      activelabmenu === 'Sensuri'
                        ? (userP ? styles.boldClass1 : styles.boldClass1)
                        : styles.orderlistLi
                    }
                    onClick={() => {
                      handleQualityControlRedirect();
                    }}
                  >
                    <div className="d-flex">
                      <div>
                        <p>{tSidebar('qualityControl', { defaultValue: 'Sensuri' })}</p>
                      </div>
                    </div>
                  </li>
                </div>
              )}
            </ul>
          </div>
        </div>
      )}



      {mainmenu === 'setting' && (
        <div className={styles.navigationsidebar}>
          <VidaiLogoIcon
            style={{
              position: 'absolute',
              bottom: '8%',
              width: '60%',
              left: '40px',
            }}
          >
          </VidaiLogoIcon>
          <VersionText   />
          <div
            className={`${styles.adminmenu} row`}
            style={{ '--bs-gutter-x': '0' } as React.CSSProperties}
          >
            <ul
              className={`${styles.sidebarunorderedList} text-muted bg-white px-0`}
            >
              <div  style={                                  
                {background: 'linear-gradient(199.55deg, #EEEEEE 20.06%, rgba(243, 243, 243, 0.547773) 75.62%, rgba(232, 234, 236, 0) 110.17%)' ,
                  borderRadius: '20px 20px 0px 0px'}
              }>

                <div className={`${styles.orderlistDiv} `} style={{borderRadius:'0px 0px 0px 0px'}}>
                  <li
                    className={`${activeSettingMenu === 'aiHub'?  styles.boldClass12: styles.orderlistLi}`}
                    onClick={() =>{ setActiveSettingMenu('aiHub'); navigate('ai/aiHub');}}
                  >
                    {tSidebar('AI Hub')}
                  </li>

                </div>


                <div className={`${styles.orderlistDiv} `} style={activeSettingMenu === 'kpi'?{borderRadius:'0px 0px 20px 0px'}:{borderRadius:'0px 0px 0px 0px'}}>
                  <li
                    className={`${activeSettingMenu === 'kpi'?  styles.boldClass12: styles.orderlistLi}`}
                    onClick={() =>{ setActiveSettingMenu('kpi'); setselectedSettingsMenu('Clinical&Lab');navigate('ai/kpi_dashboard');}}
                  >
                    {tSidebar('KPI')}
                  </li>
                </div>

                {activeSettingMenu === 'kpi'  && (
                  <div style={{backgroundColor:'#FFF'}}>
                    <ul className={`${styles.subunorderList}`}>
                      {/* Only show Clinical if permission exists, otherwise filter by allowed types if needed */}
                      <li
                        className={`${selectedSettingsMenu === 'Clinical&Lab'
                          ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                          : styles.subunorderedlistLi
                        }`}
                        onClick={() =>{ setselectedSettingsMenu('Clinical&Lab'); navigate('ai/kpi_dashboard');}}
                      >
                        {tSidebar('Clinical & Lab')}
                      </li>
                    </ul>
                  </div>
                )}


                {/* Reports Section */}
                {getModuleAccess('Settings', 'Reports') && (
                  <div className={`${styles.orderlistDiv} `} style={activeSettingMenu === 'reports'?{borderRadius:'0px 0px 20px 0px'}:{borderRadius:'0px 0px 0px 0px'}}>
                    <li
                      className={`${activeSettingMenu === 'reports'?  styles.boldClass12: styles.orderlistLi}`}
                      onClick={() =>{ setActiveSettingMenu('reports'); navigate('ai/reports/clinical');}}
                    >
                      {tSidebar('reports')}
                    </li>
                  </div>
                )}

                {/* Reports Submenus */}
                {getModuleAccess('Settings', 'Reports') && activeSettingMenu === 'reports'  && (
                  <div style={{backgroundColor:'#FFF'}}>
                    <ul className={`${styles.subunorderList}`}>
                      {/* Only show Clinical if permission exists, otherwise filter by allowed types if needed */}
                      <li
                        className={`${selectedSettingsMenu === 'clinical'
                          ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                          : styles.subunorderedlistLi
                        }`}
                        onClick={() =>{ setselectedSettingsMenu('clinical'); navigate('ai/reports/clinical');}}
                      >
                        {tSidebar('clinical')}
                      </li>
                    </ul>
                  </div>
                )}

                {/* Configuration Section */}
                {getModuleAccess('Settings', 'Configuration') && (
                  <div className={`${styles.orderlistDiv} `} style={activeSettingMenu === 'configuration'?{borderRadius:'0px 0px 20px 0px'}:{borderRadius:'0px'}}>
                    <li
                      className={`${activeSettingMenu === 'configuration'
                        ? styles.boldClass
                        : styles.orderlistLi
                      }`}
                      onClick={() => {setActiveSettingMenu('configuration'); navigate('ai/configuration/user');}}
                    >
                      {tSidebar('configuration')}
                    </li>
                  </div>
                )}
                {/* Configuration Submenus */}
                {getModuleAccess('Settings', 'Configuration') && activeSettingMenu === 'configuration' && (
                  <div className={styles.mainclass}>
                    <ul className={styles.subunorderList}>
                      {[
                        { key: 'User', name: 'User', label: tSidebar('user'), path: '/ai/configuration/user' },
                        { key: 'Consent', name: 'Consent', label: tSidebar('consent'), path: '/ai/configuration/consent' },
                        { key: 'Clinic', name: 'Clinic', label: tSidebar('clinic'), path: '/ai/configuration/clinic' },
                      // { key: 'Page', name: 'Page', label: tSidebar('page'), path: '/ai/configuration/page' },
                      ]
                        .filter(item => getModuleAccess('Settings','Configuration', item.name))
                        .map(({ key, name, label, path }) => (
                          <li
                            key={key}
                            className={
                              selectedSettingsMenu === name
                                ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                                : styles.subunorderedlistLi
                            }
                            onClick={() => {
                              setselectedSettingsMenu(name);
                              navigate(path);
                            }}
                          >
                            {label}
                          </li>
                        ))}

                      <li
                        key={'PackageConfig'}
                        className={
                          selectedSettingsMenu === 'PackageConfig'
                            ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                            : styles.subunorderedlistLi
                        }
                        onClick={() => {
                          setselectedSettingsMenu('PackageConfig');
                          navigate('/ai/configuration/package-configuration');
                        }}
                      >
                        {tSidebar('package')}
                      </li>

                      <li
                        key={'InventoryApproval'}
                        className={
                          selectedSettingsMenu === 'InventoryApproval'
                            ? `${styles.subunorderedlistLiChecked} ${styles.active}`
                            : styles.subunorderedlistLi
                        }
                        onClick={() => {
                          setselectedSettingsMenu('InventoryApproval');
                          navigate('/ai/configuration/inventory-approval');
                        }}
                      >
                        {tSidebar('inventory_approval')}
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </ul>
          </div>
        </div>
      )}

    </div>
  );
};

export default SideBarComponent;